"use client";

import { useEffect, useRef } from "react";

import { LinkifiedText } from "@/components/linkified-text/linkified-text";
import type { IFindMySchoolMapLevelBoundaryItem } from "@/providers/store/api/find-my-school/find-my-school.interface";
import { cn } from "@/shared/helpers/cn.helper";

export interface CommunityServicesListItemProps {
  item: IFindMySchoolMapLevelBoundaryItem;
  isHighlighted?: boolean;
  variant?: "desktop" | "mobile";
  onClick?: (item: IFindMySchoolMapLevelBoundaryItem) => void;
}

export function CommunityServicesListItem({
  item,
  isHighlighted = false,
  variant = "desktop",
  onClick,
}: CommunityServicesListItemProps) {
  const itemRef = useRef<HTMLDivElement>(null);
  const mobile = variant === "mobile";
  const note = item.note?.trim() ?? "";

  useEffect(() => {
    if (!isHighlighted || !itemRef.current) return;
    const el = itemRef.current;
    const id = requestAnimationFrame(() => {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [isHighlighted]);

  const handleClick = () => {
    onClick?.(item);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleClick();
  };

  return (
    <div
      ref={itemRef}
      className={cn(
        "cursor-pointer rounded-[12px] bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#520313] focus:ring-offset-2",
        mobile ? "p-3.5" : "p-4",
        isHighlighted
          ? "border-[1.5px] border-[#520313]"
          : "border border-[#DFDFDF] hover:border-[#520313]/40",
      )}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className="flex min-w-0 flex-col gap-1.5">
        <p className="m-0 text-[12px]/[16px] font-medium uppercase tracking-[0.04em] text-[#6B6667]">
          {item.mapLevelName}
        </p>
        <h3 className="m-0 text-[16px]/[22px] font-bold text-[#161515]">
          {item.name}
        </h3>
        {note ? (
          <p className="m-0 whitespace-pre-line text-[14px]/[20px] font-normal text-[#4B4849]">
            <LinkifiedText text={note} variant="findMySchool" />
          </p>
        ) : null}
      </div>
    </div>
  );
}
